/**
 * ActionSelectControl - Generic JSON Forms control for dynamic option selection
 *
 * Reads options from ui_schema to determine how to fetch data:
 * - list_action: Toolbox action to fetch all options
 * - get_action: (Optional) Toolbox action to fetch single item for rehydration
 * - label_field / value_field: Fields in response to use for display/value
 * - params: Additional params to pass to the action
 * - multi: Whether to allow multiple selections
 */

import { useToolboxQuery } from "@/api/toolbox";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/utils";
import { Entity, Spinner } from "@cohesive/ui-components";
import { ControlProps, optionIs, rankWith } from "@jsonforms/core";
import { withJsonFormsControlProps } from "@jsonforms/react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";

export interface ActionSelectOptions {
  control: "action_select";
  description?: string;
  multi?: boolean;
  list_action: string;
  get_action?: string;
  params?: Record<string, any>;
  label_field: string;
  value_field: string;
}

export interface OptionItem {
  value: string;
  label: string;
  raw?: Entity;
}

// Props for direct usage (without JSON Forms wrapper)
export interface ActionSelectDirectProps {
  value: string | string[] | undefined;
  onChange: (value: string | string[] | undefined) => void;
  options: ActionSelectOptions;
  integrationConnectionId: string;
  preloadedOptions?: OptionItem[];
  compact?: boolean;
  label?: string;
  enabled?: boolean;
  id?: string;
}

interface ActionSelectControlProps extends Omit<ControlProps, "data"> {
  data: string | string[] | undefined;
}

const ActionSelectControlComponent: React.FC<ActionSelectControlProps> = ({
  data,
  enabled,
  handleChange,
  path,
  label,
  uischema,
  visible,
  id,
}) => {
  const options = uischema.options as ActionSelectOptions | undefined;
  const isMulti = options?.multi ?? false;

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabels, setSelectedLabels] = useState<Record<string, string>>(
    {},
  );

  // Get integration connection ID from context (passed via uischema options or form context)
  const integrationConnectionId =
    (uischema.options as any)?.integrationConnectionId ?? "";

  // Extract field mappings from options
  const labelField = options?.label_field ?? "label";
  const valueField = options?.value_field ?? "value";

  // Use React Query for fetching options - only enabled when dropdown is open
  const { data: queryData, isLoading: isLoadingOptions } = useToolboxQuery<
    Entity[]
  >(
    options?.list_action && integrationConnectionId
      ? {
          integrationConnectionId,
          actionName: options.list_action,
          actionInput: options.params ?? {},
        }
      : null,
    {
      enabled: !!options?.list_action && !!integrationConnectionId,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  );

  // Map raw response to OptionItem
  // Handle entity wrapper format: { type, base, platform, data: { ...actual fields } }
  const mapToOption = useCallback(
    (item: Entity): OptionItem => {
      // Unwrap entity structure if present
      const value = String(
        item.data[valueField as keyof typeof item.data] ?? "",
      );
      const labelValue = String(
        item.data[labelField as keyof typeof item.data] ?? value,
      );
      return { value, label: labelValue, raw: item };
    },
    [labelField, valueField],
  );

  // Process query data into options
  const allOptions = useMemo(() => {
    if (!queryData) return [];

    return queryData
      .map(mapToOption)
      .filter((opt: OptionItem) => opt.value && opt.label);
  }, [queryData, mapToOption]);

  // Normalize data to array for easier handling
  const selectedValues = useMemo(() => {
    if (!data) return [];
    return Array.isArray(data) ? data : [data];
  }, [data]);

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return allOptions;
    const query = searchQuery.toLowerCase();
    return allOptions.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query),
    );
  }, [allOptions, searchQuery]);

  // Handle selection
  const handleSelect = useCallback(
    (value: string, optionLabel: string) => {
      if (isMulti) {
        const currentValues = selectedValues;
        const newValues = currentValues.includes(value)
          ? currentValues.filter((v) => v !== value)
          : [...currentValues, value];
        handleChange(path, newValues.length > 0 ? newValues : undefined);
      } else {
        handleChange(path, value);
        setOpen(false);
      }
      setSelectedLabels((prev) => ({ ...prev, [value]: optionLabel }));
    },
    [isMulti, selectedValues, handleChange, path],
  );

  // Handle clear
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      handleChange(path, undefined);
      setSelectedLabels({});
    },
    [handleChange, path],
  );

  // Handle remove single item (multi-select)
  const handleRemove = useCallback(
    (value: string, e: React.MouseEvent) => {
      e.stopPropagation();
      const newValues = selectedValues.filter((v) => v !== value);
      handleChange(path, newValues.length > 0 ? newValues : undefined);
    },
    [selectedValues, handleChange, path],
  );

  // Helper to get label for a value - checks selectedLabels first, then allOptions, then falls back to value
  const getLabelForValue = useCallback(
    (val: string) => {
      if (selectedLabels[val]) return selectedLabels[val];
      const found = allOptions.find((opt) => opt.value === val);
      return found?.label ?? val;
    },
    [selectedLabels, allOptions],
  );

  // Get display text for single select or empty state
  const displayText = useMemo(() => {
    if (isMulti) {
      return selectedValues.length > 0 ? "Add more..." : "Select...";
    }
    if (selectedValues.length === 0) {
      return "Select...";
    }
    return getLabelForValue(selectedValues[0]);
  }, [selectedValues, getLabelForValue, isMulti]);

  if (!visible) return null;

  return (
    <div className="space-y-3" id={id}>
      {/* Label and description */}
      {label && (
        <div className="space-y-1">
          <label className="text-sm font-medium leading-none">{label}</label>
          {options?.description && (
            <p className="text-[13px] text-muted-foreground">
              {options.description}
            </p>
          )}
        </div>
      )}

      {/* Selected badges for multi-select */}
      {isMulti && selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedValues.map((val) => (
            <Badge
              key={val}
              variant="secondary"
              className="h-7 pl-2.5 pr-1.5 text-[13px] font-normal gap-1.5"
            >
              {getLabelForValue(val)}
              <button
                type="button"
                className="rounded-full hover:bg-muted-foreground/20 p-0.5 -mr-0.5"
                onClick={(e) => handleRemove(val, e)}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </Badge>
          ))}
        </div>
      )}

      {/* Dropdown trigger */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled || !integrationConnectionId}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="w-full justify-between h-10 text-sm font-normal"
          >
            <span
              className={cn("text-left", {
                "text-muted-foreground": selectedValues.length === 0,
              })}
            >
              {displayText}
            </span>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedValues.length > 0 && !isMulti && (
                <X
                  className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer"
                  onClick={handleClear}
                />
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[400px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>
                {isLoadingOptions ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="md" />
                  </div>
                ) : (
                  "No results found."
                )}
              </CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => handleSelect(option.value, option.label)}
                    >
                      {isMulti && (
                        <div
                          className={cn(
                            "mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
                            isSelected
                              ? "bg-primary text-primary-foreground"
                              : "opacity-50",
                          )}
                        >
                          {isSelected && <Check className="h-3 w-3" />}
                        </div>
                      )}
                      <span>{option.label}</span>
                      {!isMulti && isSelected && (
                        <Check className="ml-auto h-4 w-4" />
                      )}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default withJsonFormsControlProps(ActionSelectControlComponent);

export const actionSelectTester = rankWith(
  10, // Higher priority than default controls
  optionIs("control", "action_select"),
);

/**
 * ActionSelectDirect - Direct usage without JSON Forms wrapper
 * Use this when you need to render the select outside of JSON Forms context
 */
export function ActionSelectDirect({
  value,
  onChange,
  options,
  integrationConnectionId,
  preloadedOptions,
  compact = false,
  label,
  enabled = true,
  id,
}: ActionSelectDirectProps) {
  const isMulti = options?.multi ?? false;

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedLabels, setSelectedLabels] = useState<Record<string, string>>(
    {},
  );

  // Extract field mappings from options
  const labelField = options?.label_field ?? "label";
  const valueField = options?.value_field ?? "value";

  // Use React Query for fetching options - only enabled when dropdown is open and no preloaded options
  const { data: queryData, isLoading: isLoadingOptions } = useToolboxQuery(
    options?.list_action && integrationConnectionId && !preloadedOptions?.length
      ? {
          integrationConnectionId,
          actionName: options.list_action,
          actionInput: options.params ?? {},
        }
      : null,
    {
      enabled:
        !!options?.list_action &&
        !!integrationConnectionId &&
        !preloadedOptions?.length,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    },
  );

  // Map raw response to OptionItem
  const mapToOption = useCallback(
    (item: Entity): OptionItem => {
      const val = String(item.data[valueField as keyof typeof item.data] ?? "");
      const lbl = String(
        item.data[labelField as keyof typeof item.data] ?? val,
      );
      return { value: val, label: lbl, raw: item };
    },
    [labelField, valueField],
  );

  // Process query data into options, or use preloaded options
  const allOptions = useMemo(() => {
    // Use preloaded options if available
    if (preloadedOptions?.length) {
      return preloadedOptions;
    }

    if (!queryData) return [];

    return queryData
      .map(mapToOption)
      .filter((opt: OptionItem) => opt.value && opt.label);
  }, [queryData, mapToOption, preloadedOptions]);

  // Normalize value to array for easier handling
  const selectedValues = useMemo(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  // Helper to get label for a value - checks selectedLabels first, then allOptions, then falls back to value
  const getLabelForValue = useCallback(
    (val: string) => {
      if (selectedLabels[val]) return selectedLabels[val];
      const found = allOptions.find((opt: OptionItem) => opt.value === val);
      return found?.label ?? val;
    },
    [selectedLabels, allOptions],
  );

  // Filter options based on search
  const filteredOptions = useMemo(() => {
    if (!searchQuery) return allOptions;
    const query = searchQuery.toLowerCase();
    return allOptions.filter(
      (opt: OptionItem) =>
        opt.label.toLowerCase().includes(query) ||
        opt.value.toLowerCase().includes(query),
    );
  }, [allOptions, searchQuery]);

  // Handle selection
  const handleSelect = useCallback(
    (val: string, optionLabel: string) => {
      if (isMulti) {
        const currentValues = selectedValues;
        const newValues = currentValues.includes(val)
          ? currentValues.filter((v) => v !== val)
          : [...currentValues, val];
        onChange(newValues.length > 0 ? newValues : undefined);
      } else {
        onChange(val);
        setOpen(false);
      }
      setSelectedLabels((prev) => ({ ...prev, [val]: optionLabel }));
    },
    [isMulti, selectedValues, onChange],
  );

  // Handle clear
  const handleClear = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(undefined);
      setSelectedLabels({});
    },
    [onChange],
  );

  // Get display text
  const displayText = useMemo(() => {
    if (selectedValues.length === 0) {
      return "Select...";
    }
    return getLabelForValue(selectedValues[0]);
  }, [selectedValues, getLabelForValue]);

  return (
    <div className={cn(compact ? "" : "space-y-3")} id={id}>
      {/* Label - only show if not compact */}
      {label && !compact && (
        <div className="space-y-1">
          <label className="text-sm font-medium leading-none">{label}</label>
          {options?.description && (
            <p className="text-[13px] text-muted-foreground">
              {options.description}
            </p>
          )}
        </div>
      )}

      {/* Dropdown trigger */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            disabled={!enabled || !integrationConnectionId}
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between text-sm font-normal",
              compact ? "h-9" : "h-10",
            )}
          >
            <span
              className={cn("text-left truncate", {
                "text-muted-foreground": selectedValues.length === 0,
              })}
            >
              {displayText}
            </span>
            <div className="flex items-center gap-1 ml-2 shrink-0">
              {selectedValues.length > 0 && (
                <div aria-label="Clear selection" onClick={handleClear}>
                  <X className="h-4 w-4 opacity-50 hover:opacity-100 cursor-pointer" />
                </div>
              )}
              <ChevronsUpDown className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[250px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search..."
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <CommandList>
              <CommandEmpty>
                {isLoadingOptions ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="md" />
                  </div>
                ) : (
                  "No results found."
                )}
              </CommandEmpty>
              <CommandGroup>
                {filteredOptions.map((option: OptionItem) => {
                  const isSelected = selectedValues.includes(option.value);
                  return (
                    <CommandItem
                      key={option.value}
                      value={`${option.label} ${option.value}`}
                      onSelect={() => handleSelect(option.value, option.label)}
                    >
                      <span className="flex-1 truncate">{option.label}</span>
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
}
